import React, { useState, useCallback } from 'react';
import apiService from '../../services/api';

// Onboarding screen content constants for easy future translation
const ONBOARDING_CONTENT = {
  welcome: {
    title: 'Welcome to Grid Vault',
    subtitle: 'Your automated DeFi yield strategy on Solana',
  },
  howItWorks: {
    title: 'How It Works',
    steps: [
      {
        icon: '$',
        title: 'Fund Your Wallet',
        description: 'Deposit SOL first for gas fees, then add SOL or USDC for trading',
      },
      {
        icon: '^',
        title: 'Earn Trading Fees',
        description: 'The bot provides liquidity on Orca and earns fees',
      },
      {
        icon: '#',
        title: 'Delta Hedged',
        description: 'Price risk is reduced via Drift perpetuals',
      },
    ],
  },
  solRequirement: {
    title: 'Important: SOL First',
    warning: 'Your first deposit must be SOL',
    reasons: [
      'SOL is required to pay transaction fees (gas) on Solana',
      'Without SOL, you cannot swap, trade, or perform any operations',
      'Deposit at least 0.1 SOL first, then add SOL or USDC for trading',
      'SOL fees are very low - 0.1 SOL lasts for hundreds of transactions',
    ],
    acknowledgment: 'I understand SOL is needed for gas fees',
  },
  riskDisclosure: {
    title: 'Important Risks',
    warning: 'This is not a savings account',
    risks: [
      'Past performance does not guarantee future results',
      'Smart contract risk exists',
      'Impermanent loss may occur',
      'Only deposit what you can afford to lose',
    ],
    acknowledgment: 'I understand and accept these risks',
  },
  getStarted: {
    title: "You're Ready!",
    subtitle: 'Next steps:',
    steps: [
      'Subscribe to a plan',
      'Deposit SOL first (for gas fees)',
      'Add SOL or USDC for trading capital',
      'Click "Start Bot" and watch your vault grow',
    ],
    cta: 'Go to Vault',
  },
};

interface OnboardingFlowProps {
  onComplete: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);
  const [solRequirementAcknowledged, setSolRequirementAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalScreens = 5;

  const handleNext = useCallback(() => {
    if (currentScreen < totalScreens - 1) {
      setCurrentScreen(currentScreen + 1);
    }
  }, [currentScreen]);

  const handleBack = useCallback(() => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  }, [currentScreen]);

  const handleComplete = useCallback(async () => {
    if (!riskAcknowledged) {
      setError('You must acknowledge the risks to continue.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiService.completeOnboarding(true);
      onComplete();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete onboarding. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [riskAcknowledged, onComplete]);

  const renderProgressDots = () => (
    <div className="flex justify-center gap-2 mt-8">
      {Array.from({ length: totalScreens }).map((_, index) => (
        <button
          key={index}
          onClick={() => {
            // Navigation logic:
            // - Screen 0-1: Always accessible
            // - Screen 2 (SOL requirement): Can navigate after viewing
            // - Screen 3 (risk disclosure): Can navigate after SOL acknowledgment
            // - Screen 4 (get started): Can navigate after both acknowledgments
            if (index < 2) {
              setCurrentScreen(index);
            } else if (index === 2 && currentScreen >= 2) {
              setCurrentScreen(index);
            } else if (index === 3 && solRequirementAcknowledged) {
              setCurrentScreen(index);
            } else if (index === 4 && solRequirementAcknowledged && riskAcknowledged) {
              setCurrentScreen(index);
            }
          }}
          className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
            index === currentScreen
              ? 'bg-emerald-500 scale-125'
              : index < currentScreen
              ? 'bg-emerald-500/50'
              : 'bg-gray-600'
          }`}
          aria-label={`Go to screen ${index + 1}`}
        />
      ))}
    </div>
  );

  const renderWelcomeScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-emerald-500/30">
        <span className="text-3xl text-white font-bold">G</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
        {ONBOARDING_CONTENT.welcome.title}
      </h1>
      <p className="text-gray-400 text-lg mb-8 max-w-md">
        {ONBOARDING_CONTENT.welcome.subtitle}
      </p>
      <button
        onClick={handleNext}
        className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/30"
      >
        Continue
      </button>
      {renderProgressDots()}
    </div>
  );

  const renderHowItWorksScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
        {ONBOARDING_CONTENT.howItWorks.title}
      </h2>
      <div className="space-y-6 w-full max-w-md">
        {ONBOARDING_CONTENT.howItWorks.steps.map((step, index) => (
          <div
            key={index}
            className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700/50"
          >
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-emerald-400 text-xl font-bold">{index + 1}</span>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">{step.title}</h3>
              <p className="text-gray-400 text-sm">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-8">
        <button
          onClick={handleBack}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/30"
        >
          Continue
        </button>
      </div>
      {renderProgressDots()}
    </div>
  );

  const renderSolRequirementScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-6">
        <span className="text-purple-400 text-2xl font-bold">SOL</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
        {ONBOARDING_CONTENT.solRequirement.title}
      </h2>
      <p className="text-purple-400 font-medium mb-6 text-center">
        {ONBOARDING_CONTENT.solRequirement.warning}
      </p>
      <div className="space-y-3 w-full max-w-md mb-8">
        {ONBOARDING_CONTENT.solRequirement.reasons.map((reason, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-purple-500/30"
          >
            <span className="text-purple-400 font-bold">{index + 1}</span>
            <p className="text-gray-300 text-sm">{reason}</p>
          </div>
        ))}
      </div>
      <label className="flex items-center gap-3 p-4 bg-gray-800 rounded-xl border border-gray-700 cursor-pointer hover:border-purple-500/50 transition-colors w-full max-w-md mb-4">
        <input
          type="checkbox"
          checked={solRequirementAcknowledged}
          onChange={(e) => {
            setSolRequirementAcknowledged(e.target.checked);
            setError(null);
          }}
          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
        />
        <span className="text-white font-medium">
          {ONBOARDING_CONTENT.solRequirement.acknowledgment}
        </span>
      </label>
      {error && (
        <p className="text-red-400 text-sm mb-4">{error}</p>
      )}
      <div className="flex gap-4 mt-4">
        <button
          onClick={handleBack}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!solRequirementAcknowledged}
          className={`px-8 py-3 font-semibold rounded-xl transition-all duration-200 ${
            solRequirementAcknowledged
              ? 'bg-purple-500 hover:bg-purple-600 text-white shadow-lg shadow-purple-500/30'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
      {renderProgressDots()}
    </div>
  );

  const renderRiskDisclosureScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6">
        <span className="text-amber-400 text-3xl">!</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
        {ONBOARDING_CONTENT.riskDisclosure.title}
      </h2>
      <p className="text-amber-400 font-medium mb-6 text-center">
        {ONBOARDING_CONTENT.riskDisclosure.warning}
      </p>
      <div className="space-y-3 w-full max-w-md mb-8">
        {ONBOARDING_CONTENT.riskDisclosure.risks.map((risk, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50"
          >
            <span className="text-gray-400">*</span>
            <p className="text-gray-300 text-sm">{risk}</p>
          </div>
        ))}
      </div>
      <label className="flex items-center gap-3 p-4 bg-gray-800 rounded-xl border border-gray-700 cursor-pointer hover:border-emerald-500/50 transition-colors w-full max-w-md mb-4">
        <input
          type="checkbox"
          checked={riskAcknowledged}
          onChange={(e) => {
            setRiskAcknowledged(e.target.checked);
            setError(null);
          }}
          className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
        />
        <span className="text-white font-medium">
          {ONBOARDING_CONTENT.riskDisclosure.acknowledgment}
        </span>
      </label>
      {error && (
        <p className="text-red-400 text-sm mb-4">{error}</p>
      )}
      <div className="flex gap-4 mt-4">
        <button
          onClick={handleBack}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!riskAcknowledged}
          className={`px-8 py-3 font-semibold rounded-xl transition-all duration-200 ${
            riskAcknowledged
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
      {renderProgressDots()}
    </div>
  );

  const renderGetStartedScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mb-8 shadow-lg shadow-emerald-500/30">
        <span className="text-white text-4xl">&#10003;</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
        {ONBOARDING_CONTENT.getStarted.title}
      </h2>
      <p className="text-gray-400 mb-6">{ONBOARDING_CONTENT.getStarted.subtitle}</p>
      <div className="space-y-3 w-full max-w-md mb-8">
        {ONBOARDING_CONTENT.getStarted.steps.map((step, index) => (
          <div
            key={index}
            className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50"
          >
            <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold text-sm">
              {index + 1}
            </span>
            <p className="text-gray-300">{step}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <button
          onClick={handleBack}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleComplete}
          disabled={isSubmitting}
          className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Loading...' : ONBOARDING_CONTENT.getStarted.cta}
        </button>
      </div>
      {error && (
        <p className="text-red-400 text-sm mt-4">{error}</p>
      )}
      {renderProgressDots()}
    </div>
  );

  const screens = [
    renderWelcomeScreen,
    renderHowItWorksScreen,
    renderSolRequirementScreen,
    renderRiskDisclosureScreen,
    renderGetStartedScreen,
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex flex-col">
      <div className="flex-1 flex flex-col justify-center py-8">
        {screens[currentScreen]()}
      </div>
    </div>
  );
};

export default OnboardingFlow;
